import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ArrowRight, ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingButtons from "@/components/FloatingButtons";
import { BLOG_POSTS, CATEGORIES } from "@/lib/blogData";
import usePageMeta from "@/hooks/usePageMeta";

export default function BlogList() {
  const [category, setCategory] = useState("Todos");
  usePageMeta({
    title: "Blog · Great Solutions US · Guías de impuestos, ITIN, LLC y notaría",
    description: "Artículos y guías prácticas sobre impuestos, ITIN, formación de LLC, notarizaciones y trámites migratorios en Florida por Yohaly Acevedo.",
  });

  const posts =
    category === "Todos" ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === category);

  return (
    <div className="min-h-screen bg-white" data-testid="blog-list">
      <Navbar />
      <main className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#475569] hover:text-[#0B2D5C] mb-6">
            <ArrowLeft size={16} />
            Volver al inicio
          </Link>

          <div className="max-w-3xl mb-12">
            <div className="text-xs font-semibold text-[#0B2D5C] tracking-[0.2em] uppercase mb-4">
              Blog · Recursos gratis
            </div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#0f172a] leading-tight mb-5">
              Aprende sobre <span className="text-[#0B2D5C]">impuestos, negocios e inmigración</span>
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed">
              Guías prácticas y respuestas claras a las preguntas más comunes de nuestra comunidad.
            </p>
          </div>

          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-10 border-b border-[#e2e8f0] pb-4">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                className={`px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
                  category === c
                    ? "bg-[#0B2D5C] text-white"
                    : "bg-[#f8fafc] text-[#475569] hover:bg-[#e2e8f0]"
                }`}
                data-testid={`blog-cat-${c}`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="group border border-[#e2e8f0] rounded-sm overflow-hidden hover:border-[#C89B2C] hover:-translate-y-1 transition-all duration-300 flex flex-col bg-white"
                data-testid={`blog-post-${p.slug}`}
              >
                <div className="aspect-video overflow-hidden bg-[#f8fafc]">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[10px] font-bold text-[#0B2D5C] bg-[#0B2D5C]/10 px-2 py-1 rounded-sm tracking-wider uppercase">
                      {p.category}
                    </span>
                    <span className="text-xs text-[#475569] flex items-center gap-1">
                      <Clock size={12} /> {p.readTime}
                    </span>
                  </div>
                  <h3 className="font-display text-lg font-semibold text-[#0f172a] leading-tight mb-2 group-hover:text-[#0B2D5C] transition-colors">
                    {p.title}
                  </h3>
                  <p className="text-sm text-[#475569] leading-relaxed mb-4 flex-1">{p.excerpt}</p>
                  <div className="pt-4 border-t border-[#e2e8f0] flex justify-between items-center">
                    <span className="text-xs text-[#475569]">{p.date}</span>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#0B2D5C] group-hover:gap-2 transition-all">
                      Leer <ArrowRight size={12} />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {posts.length === 0 && (
            <div className="text-center py-16 text-[#475569]">Aún no hay artículos en esta categoría.</div>
          )}
        </div>
      </main>
      <Footer />
      <FloatingButtons />
    </div>
  );
}
